import SEOHead from '../components/SEOHead';

const Section = ({ title, children }) => (
  <div className="mt-8">
    <h2 className="text-xl font-semibold text-slate-900">{title}</h2>
    <div className="mt-3 space-y-3 text-slate-600 leading-relaxed">{children}</div>
  </div>
);

const PrivacyPolicy = () => (
  <>
    <SEOHead
      title="Privacy Policy"
      description="How IDGate handles data for operators and guests using our Aadhaar check-in platform."
      url="https://idgate.in/privacy"
      noindex
    />

    <div className="border-b border-slate-200 bg-white">
      <div className="container-custom py-16 lg:py-20">
        <div className="mx-auto max-w-3xl">
<h1 className="text-4xl font-bold tracking-tight text-slate-900">Privacy Policy</h1>
          <p className="mt-3 text-slate-500 text-sm">Effective date: 15 April 2026</p>
        </div>
      </div>
    </div>

    <div className="bg-surface-page py-16">
      <div className="container-custom">
        <div className="mx-auto max-w-3xl">

          <Section title="Overview">
            <p>
              IDGate operates a QR-based Aadhaar check-in platform for regulated businesses. This
              policy explains what data we collect, how we use it, and the protections we apply.
              IDGate is designed around a core principle: <strong>no Aadhaar number, biometric, or
              raw personal data is stored on our servers</strong>.
            </p>
          </Section>

          <Section title="How identity verification works">
            <p>
              Identity verification is performed entirely through UIDAI&rsquo;s official Aadhaar
              authentication infrastructure. When a guest scans a check-in QR code, the Aadhaar
              app on their device sends a verification request directly to UIDAI. UIDAI returns a
              yes/no outcome and a minimal verified data packet (name, masked mobile, photo token)
              to IDGate under the guest&rsquo;s explicit, app-level consent.
            </p>
            <p>
              IDGate never receives, processes, or stores: Aadhaar numbers, biometrics, OTPs, or
              any authentication credentials. The guest&rsquo;s consent is captured in the Aadhaar app —
              not on IDGate&rsquo;s systems.
            </p>
          </Section>

          <Section title="Data we collect">
            <p><strong>From operators (businesses using IDGate):</strong></p>
            <ul className="mt-2 list-disc pl-5 space-y-1">
              <li>Account information: name, work email, organisation name</li>
              <li>Flow configuration: custom field definitions, webhook URLs, Google Sheet IDs</li>
              <li>Billing information (processed by our payment provider; not stored by IDGate)</li>
            </ul>
            <p className="mt-3"><strong>From guests (individuals checking in):</strong></p>
            <ul className="mt-2 list-disc pl-5 space-y-1">
              <li>UIDAI-verified name and tokenised identity reference</li>
              <li>Responses to the operator&rsquo;s custom fields (e.g. room number, vehicle plate)</li>
              <li>Check-in timestamp and flow identifier</li>
            </ul>
            <p className="mt-3">
              This merged record is processed in memory and delivered immediately to the
              operator&rsquo;s configured webhook endpoint or Google Sheet. <strong>IDGate does not
              persistently store guest check-in records</strong> once delivery to the operator&rsquo;s
              storage is confirmed. The operator owns the record and is responsible for its
              retention and security from that point.
            </p>
            <p className="mt-3">
              If an operator does not configure an external storage endpoint, IDGate can act as
              a secure data custodian and retain records on the operator&rsquo;s behalf. In this
              case, the operator remains the data fiduciary and may export or delete records
              from the console at any time.
            </p>
          </Section>

          <Section title="Data ownership and DPDP Act compliance">
            <p>
              IDGate is a <strong>data processor</strong>, not a data fiduciary, with respect to
              guest check-in records. The operator is the data fiduciary under the Digital
              Personal Data Protection (DPDP) Act, 2023. This means:
            </p>
            <ul className="mt-2 list-disc pl-5 space-y-1">
              <li>Operators own all check-in records delivered to their systems.</li>
              <li>Operators determine the purpose and duration of data retention.</li>
              <li>Operators are responsible for informing guests about their data practices.</li>
              <li>IDGate processes data only as instructed by the operator&rsquo;s flow configuration.</li>
            </ul>
            <p className="mt-3">
              Guest verification is consent-led: the Aadhaar app presents a clear consent screen
              before any data is shared. Operators configure exactly which custom fields guests
              fill. IDGate does not add, modify, or retain any guest data beyond what is needed
              to deliver the record to the operator.
            </p>
          </Section>

          <Section title="Data sharing">
            <p>
              IDGate does not sell personal data. Guest check-in records are delivered only to
              the operator who configured the flow and are not accessible to any other party.
              We use sub-processors (cloud infrastructure) under data processing agreements
              solely for the purpose of transmitting records to operator-configured endpoints.
              A current list of sub-processors is available on request.
            </p>
          </Section>

          <Section title="Data retention and deletion">
            <p>
              <strong>Guest check-in records:</strong> Delivered to the operator&rsquo;s storage and
              not retained by IDGate. If IDGate is acting as custodian at the operator&rsquo;s
              request, records are retained per the operator&rsquo;s plan settings. Operators can
              export or delete records via the console at any time. Guests should contact the
              operator directly to exercise deletion rights over their check-in record.
            </p>
            <p className="mt-3">
              <strong>Operator account data</strong> is retained while the account is active
              and for 90 days after closure.
            </p>
          </Section>

          <Section title="Security">
            <p>
              All data in transit is encrypted using TLS 1.2+. Data at rest is encrypted using
              AES-256. Access to production systems is restricted to authorised personnel with
              multi-factor authentication. We undergo periodic security reviews.
            </p>
          </Section>

          <Section title="Contact">
            <p>
              For privacy queries, data deletion requests, or to report a concern, contact us at{' '}
              <a href="mailto:sales@idgate.in" className="text-primary-700 hover:underline">
                sales@idgate.in
              </a>
              .
            </p>
          </Section>

        </div>
      </div>
    </div>
  </>
);

export default PrivacyPolicy;
